/** @jsxImportSource react */
import {
  Box,
  Grid,
  Typography,
  Paper,
  Icon,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
} from "@mui/material";
import { StepProps } from "./types";
import { StyledTextField, StyledFormControl } from "./StyledComponents";

export const SettingsStep: React.FC<StepProps> = ({ formik }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure the basic settings for the business
      </Typography>

      <Grid container spacing={3}>
        {/* Regional Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <Icon color="primary">public</Icon>
              Regional Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">schedule</Icon>
                    Timezone
                  </Typography>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="settings.timezone"
                      value={formik.values.settings.timezone}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="America/New_York">Eastern Time</MenuItem>
                      <MenuItem value="America/Chicago">Central Time</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time</MenuItem>
                      <MenuItem value="America/Los_Angeles">
                        Pacific Time
                      </MenuItem>
                    </Select>
                  </StyledFormControl>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">language</Icon>
                    Language
                  </Typography>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="settings.language"
                      value={formik.values.settings.language}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                    </Select>
                  </StyledFormControl>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">attach_money</Icon>
                    Currency
                  </Typography>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="settings.currency"
                      value={formik.values.settings.currency}
                      onChange={formik.handleChange}
                    >
                      <MenuItem
                        value="BHD"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/bh.png"
                          alt="BH"
                          sx={{ width: 20, height: "auto" }}
                        />
                        BHD - Bahraini Dinar
                      </MenuItem>
                      <MenuItem
                        value="SAR"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/sa.png"
                          alt="SA"
                          sx={{ width: 20, height: "auto" }}
                        />
                        SAR - Saudi Riyal
                      </MenuItem>
                      <MenuItem
                        value="AED"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/ae.png"
                          alt="AE"
                          sx={{ width: 20, height: "auto" }}
                        />
                        AED - UAE Dirham
                      </MenuItem>
                      <MenuItem
                        value="KWD"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/kw.png"
                          alt="KW"
                          sx={{ width: 20, height: "auto" }}
                        />
                        KWD - Kuwaiti Dinar
                      </MenuItem>
                      <MenuItem
                        value="QAR"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/qa.png"
                          alt="QA"
                          sx={{ width: 20, height: "auto" }}
                        />
                        QAR - Qatari Riyal
                      </MenuItem>
                      <MenuItem
                        value="OMR"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/om.png"
                          alt="OM"
                          sx={{ width: 20, height: "auto" }}
                        />
                        OMR - Omani Rial
                      </MenuItem>
                      <MenuItem
                        value="EGP"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/eg.png"
                          alt="EG"
                          sx={{ width: 20, height: "auto" }}
                        />
                        EGP - Egyptian Pound
                      </MenuItem>
                      <MenuItem
                        value="USD"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/us.png"
                          alt="US"
                          sx={{ width: 20, height: "auto" }}
                        />
                        USD - US Dollar
                      </MenuItem>
                      <MenuItem
                        value="EUR"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/w20/eu.png"
                          alt="EU"
                          sx={{ width: 20, height: "auto" }}
                        />
                        EUR - Euro
                      </MenuItem>
                    </Select>
                  </StyledFormControl>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Tax Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <Icon color="primary">receipt</Icon>
              Tax Settings
            </Typography>
            <Box
              sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.default",
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Icon color="action">check_circle</Icon>
                <FormControlLabel
                  control={
                    <Switch
                      name="settings.tax_settings.tax_included"
                      checked={formik.values.settings.tax_settings.tax_included}
                      onChange={formik.handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2">
                        Tax Included in Prices
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        All displayed prices will include tax
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  minWidth: 200,
                }}
              >
                <Icon color="action">percent</Icon>
                <StyledTextField
                  fullWidth
                  size="small"
                  type="number"
                  name="settings.tax_settings.default_tax_rate"
                  label="Default Tax Rate"
                  value={formik.values.settings.tax_settings.default_tax_rate}
                  onChange={formik.handleChange}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
